const getDateKey = (item) => item?.dateadd?.split("T")[0].split(" ")[0] || item?.dateadd || "";

const getRecordKey = (item) => {
  const campaignId = String(item?.campaignid ?? "").trim();
  if (campaignId) return `campaign:${campaignId}`;

  const rawDate = String(item?.dateadd ?? "").trim();
  if (rawDate) return `dateadd:${rawDate}`;

  const date = getDateKey(item);
  return date ? `date:${date}` : "";
};

const getRecordSortTime = (item) => {
  const rawDate = String(item?.dateadd ?? "").trim();
  const timestamp = new Date(rawDate || getDateKey(item) || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const getLabDisplayValue = (item) => {
  if (!item) return "";

  const result = String(item.result ?? "").trim();
  if (result) {
    const numericResult = result.match(/-?\d+(?:\.\d+)?/);
    if (numericResult) return numericResult[0];
  }

  const value = String(item.value ?? "").trim();
  if (value) {
    const numericValue = value.match(/-?\d+(?:\.\d+)?/);
    if (numericValue) return numericValue[0];
  }

  const data = String(item.data ?? "").trim();
  if (data) {
    const numericData = data.match(/-?\d+(?:\.\d+)?/);
    if (numericData) return numericData[0];
  }

  return "";
};

export const getLabRawValue = (item) => {
  if (!item) return "";

  const result = String(item.result ?? "").trim();
  if (result) return result;

  const value = String(item.value ?? "").trim();
  if (value) return value;

  const data = String(item.data ?? "").trim();
  if (data) return data;

  return "";
};

export const buildLatestCombinedRecord = (fieldEntries = [], valueSelector = getLabRawValue) => {
  const recordsMap = new Map();

  fieldEntries.forEach(({ field, items }) => {
    (items || []).forEach((item) => {
      const recordKey = getRecordKey(item);
      if (!recordKey) return;

      if (!recordsMap.has(recordKey)) {
        recordsMap.set(recordKey, {
          date: getDateKey(item),
          rawDate: item.dateadd,
          sortTime: getRecordSortTime(item),
        });
      }

      const record = recordsMap.get(recordKey);
      const value = valueSelector(item);
      const itemSortTime = getRecordSortTime(item);
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        (record[field] == null || itemSortTime >= (record[`${field}SortTime`] || 0))
      ) {
        record[field] = value;
        record[`${field}SortTime`] = itemSortTime;
      }
      if (!record.rawDate || itemSortTime >= record.sortTime) {
        record.rawDate = item.dateadd;
        record.sortTime = itemSortTime;
      }
    });
  });

  return Array.from(recordsMap.values())
    .sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0))[0] || null;
};

export const fetchLabDataByNameFallbacks = async (getLabDataByName, candidates = []) => {
  const expandedCandidates = [];
  (candidates || []).filter(Boolean).forEach((name) => {
    const rawName = String(name).trim();
    const compactName = rawName.toLowerCase().replace(/[\s_-]+/g, "");

    expandedCandidates.push(rawName);
    if (compactName && compactName !== rawName.toLowerCase()) {
      expandedCandidates.push(compactName);
    }
  });

  const uniqueCandidates = [...new Set(expandedCandidates)];
  if (uniqueCandidates.length === 0) return [];

  const responses = await Promise.allSettled(
    uniqueCandidates.map((name) => getLabDataByName(name)),
  );

  const merged = [];
  const seen = new Set();

  for (const response of responses) {
    if (response.status !== "fulfilled") continue;

    const items = response.value?.data || [];
    for (const item of items) {
      if (!item) continue;

      const dedupeKey = [
        item.id ?? "",
        item.dateadd ?? "",
        item.name ?? "",
        item.value ?? "",
        item.memberid ?? "",
        item.campaignid ?? "",
      ].join("|");

      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      merged.push(item);
    }
  }

  return merged.sort((a, b) => new Date(b.dateadd) - new Date(a.dateadd));
};
